import { NEW_CLIENTS_ONLY_HELP, newClientsOnlyLabel, type BusinessRules, type PackageCard } from "@teamna/shared";
import { Link } from "@tanstack/react-router";
import { useMemo, useState, type ReactNode } from "react";
import {
  Badge,
  Button,
  CellField,
  ChipRow,
  Field,
  FormRow,
  FormSection,
  InlineError,
  Notice,
  SkeletonList,
  TrashIcon,
  showToast,
} from "../../components/index.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { usePackagesAndRules, useSavePackages } from "@teamna/web-client/hooks/use-setup.ts";
import { formatMoney } from "@teamna/web-client/lib/format.ts";
import { SaveBar, SetupFrame } from "./frame.tsx";

/**
 * Setup step 5: packages and the cancellation rule (mobile spec 5.3, "Define packages & rules").
 *
 * The package cards as on mobile, pre-filled from the sport template until something is saved, and
 * the business-wide rule block with the sentence members read, updated as the owner picks. Saved
 * with one `PUT /packages`, which is what makes the step done. Hiding a package or changing one on
 * its own stays on the Packages page, which this page links to.
 */
export function SetupPackagesScreen() {
  return <SetupFrame step="packages">{(businessId) => <PackagesBody businessId={businessId} />}</SetupFrame>;
}

type Draft = PackageCard & { key: string };

let counter = 0;
const nextKey = () => `p${++counter}`;

/** "Cancel more than 1 day before a session and the session goes back. After that, the session is lost." */
export function rulePreview(rules: BusinessRules): string {
  const hours = rules.freeCancelCutoffHours;
  if (hours === 0) {
    return "Cancel any time before a session starts and the session goes back.";
  }
  const cutoff =
    hours >= 24 && hours % 24 === 0
      ? `${hours / 24} ${hours === 24 ? "day" : "days"}`
      : `${hours} ${hours === 1 ? "hour" : "hours"}`;
  return `Cancel more than ${cutoff} before a session and the session goes back. After that, ${
    rules.lateCancelRule === "session_lost" ? "the session is lost" : "the session still goes back"
  }.`;
}

function PackagesBody({ businessId }: { businessId: string }) {
  const { label } = useLabels();
  const current = usePackagesAndRules(businessId);
  const save = useSavePackages(businessId);
  // The saved list (or the sport template) until the owner changes something.
  const initial = useMemo(
    () => current.data?.packages.map(({ active: _active, ...pkg }) => ({ ...pkg, key: nextKey() })) ?? null,
    [current.data],
  );
  const [edited, setEdited] = useState<Draft[] | null>(null);
  const [editedRules, setRules] = useState<BusinessRules | null>(null);
  const drafts = edited ?? initial;
  const rules = editedRules ?? current.data?.rules ?? null;
  const setDrafts = (change: (list: Draft[] | null) => Draft[]) =>
    setEdited((list) => change(list ?? initial));
  const [problem, setProblem] = useState<string | null>(null);

  if (current.isLoading || !drafts || !rules) return <SkeletonList rows={3} />;

  function update(key: string, patch: Partial<PackageCard>) {
    setDrafts((list) => (list ?? []).map((row) => (row.key === key ? Object.assign({}, row, patch) : row)));
  }

  function add(kind: "group" | "private") {
    setDrafts((list) => [
      ...(list ?? []),
      {
        key: nextKey(),
        id: null,
        name: kind === "group" ? "Monthly, 8 sessions" : "Private, 4 sessions",
        kind,
        sessionsCount: kind === "group" ? 8 : 4,
        priceFils: kind === "group" ? 65_000 : 100_000,
        validityDays: 30,
        autoRenew: false,
      },
    ]);
  }

  function submit() {
    if (!drafts || !rules) return;
    const bad = drafts.find(
      (row) => !row.name.trim() || row.sessionsCount < 1 || row.priceFils < 0 || row.validityDays < 1,
    );
    if (bad) {
      setProblem("Every package needs a name, at least one session, a price and the days it is valid for.");
      return;
    }
    setProblem(null);
    save.mutate(
      {
        packages: drafts.map(({ key: _key, ...pkg }) => ({
          ...pkg,
          name: pkg.name.trim(),
          id: pkg.id?.startsWith("00000000-") ? null : (pkg.id ?? null),
        })),
        rules,
      },
      {
        onSuccess: () => {
          // Back to the saved list, so a second save updates the new rows instead of adding them again.
          setEdited(null);
          setRules(null);
          showToast("Packages and the cancellation rule are saved");
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {current.data?.fromTemplate ? (
        <Notice tone="info" title="Starter packages for your sport">
          Change the numbers, add or remove packages, then save. Nothing is on sale until you save.
        </Notice>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {drafts.map((pkg) => (
          <section
            key={pkg.key}
            className="flex flex-col gap-3 rounded-card border border-line bg-paper p-5 shadow-card"
          >
            <div className="flex items-center gap-2">
              <Badge tone={pkg.kind === "private" ? "quiet" : "confirmed"}>
                {pkg.kind === "private" ? "Private" : label("group", { capital: true })}
              </Badge>
              {pkg.newClientsOnly ? <Badge tone="late">{newClientsOnlyLabel(label("member", { plural: true }))}</Badge> : null}
              <span className="min-w-0 flex-1 truncate text-[13px] text-ink-3">
                {formatMoney(pkg.priceFils)}, {pkg.sessionsCount} sessions
              </span>
              <button
                type="button"
                aria-label={`Remove ${pkg.name}`}
                onClick={() => setDrafts((list) => (list ?? []).filter((row) => row.key !== pkg.key))}
                className="flex size-9 items-center justify-center rounded-lg text-ink-3 hover:bg-surface hover:text-cancelled"
              >
                <TrashIcon size={17} />
              </button>
            </div>
            <div className="flex flex-col gap-1 text-[13px] font-semibold text-ink-2">
              <span aria-hidden="true">Name</span>
              <CellField
                label="Package name"
                value={pkg.name}
                onChange={(event) => update(pkg.key, { name: event.target.value })}
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="flex flex-col gap-1 text-[13px] font-semibold text-ink-2">
                <span aria-hidden="true">Sessions</span>
                <CellField
                  label={`Sessions in ${pkg.name}`}
                  type="number"
                  min={1}
                  value={pkg.sessionsCount}
                  onChange={(event) => update(pkg.key, { sessionsCount: Number(event.target.value) || 0 })}
                />
              </div>
              <div className="flex flex-col gap-1 text-[13px] font-semibold text-ink-2">
                <span aria-hidden="true">Price, AED</span>
                <CellField
                  label={`Price of ${pkg.name} in AED`}
                  type="number"
                  min={0}
                  value={Math.round(pkg.priceFils / 100)}
                  onChange={(event) =>
                    update(pkg.key, { priceFils: (Number(event.target.value) || 0) * 100 })
                  }
                />
              </div>
              <div className="flex flex-col gap-1 text-[13px] font-semibold text-ink-2">
                <span aria-hidden="true">Valid, days</span>
                <CellField
                  label={`Days ${pkg.name} is valid`}
                  type="number"
                  min={1}
                  value={pkg.validityDays}
                  onChange={(event) => update(pkg.key, { validityDays: Number(event.target.value) || 0 })}
                />
              </div>
            </div>
            {pkg.newClientsOnly ? <p className="text-[13px] text-ink-2">{NEW_CLIENTS_ONLY_HELP}</p> : null}
            {pkg.kind === "private" ? (
              <p className="text-[13px] text-ink-3">
                Books only the private slots in your schedule, one {label("participant")} for each slot.
              </p>
            ) : null}
          </section>
        ))}
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        <Button variant="secondary" onClick={() => add("group")}>
          Add a {label("group")} package
        </Button>
        <Button variant="secondary" onClick={() => add("private")}>
          Add a private package
        </Button>
      </div>

      <RulesSection rules={rules} onChange={setRules} />

      {problem ? <Notice tone="warning" title={problem} /> : null}

      <SaveBar>
        <Button loading={save.isPending} disabled={drafts.length === 0} onClick={submit}>
          Save packages
        </Button>
      </SaveBar>
      {save.error ? <InlineError error={save.error} /> : null}

      <p className="text-[14px] text-ink-2">
        To hide a package or change one at a time, open{" "}
        <Link to="/owner/packages" className="font-semibold text-accent-strong underline">
          Packages and rules
        </Link>
        .
      </p>
    </div>
  );
}

/** The cut-offs most businesses pick. Any other whole number of hours goes in through "Other". */
export const CUTOFF_HOURS = [2, 6, 12, 24, 48];
/** The longest cut-off the API accepts: 30 days. */
const MAX_CUTOFF_HOURS = 720;

/**
 * The business-wide cancellation rule: the cut-off chips with "Other" for any number of hours, the
 * late rule chips, the sentence members read, updated as the owner picks, and the fixed note that a
 * business cancellation always returns the session. Shared by this step and the Packages and rules
 * page; each saves it its own way.
 */
export function RulesSection({
  rules,
  onChange,
  footer,
}: {
  rules: BusinessRules;
  onChange: (next: BusinessRules) => void;
  /** The page's own save button, when the rule saves on its own. */
  footer?: ReactNode;
}) {
  const { label } = useLabels();
  // A cut-off the chips do not offer (saved on the phone, or typed here) opens on "Other".
  const [other, setOther] = useState(() => !CUTOFF_HOURS.includes(rules.freeCancelCutoffHours));
  /*
   * The field always shows the rule's own value, so it can never show a number other than the one
   * that will be saved. The one exception is while the owner has emptied it to type a new number:
   * then it is blank, says the rule has not changed, and shows the value again when they leave it.
   */
  const [emptied, setEmptied] = useState(false);
  const showOther = other || !CUTOFF_HOURS.includes(rules.freeCancelCutoffHours);

  function pick(id: string) {
    setEmptied(false);
    if (id === "other") {
      setOther(true);
      return;
    }
    setOther(false);
    onChange({ ...rules, freeCancelCutoffHours: Number(id) });
  }

  function type(value: string) {
    const digits = value.replace(/[^0-9]/g, "").slice(0, 3);
    setEmptied(digits === "");
    if (digits === "") return;
    onChange({ ...rules, freeCancelCutoffHours: Math.min(MAX_CUTOFF_HOURS, Number(digits)) });
  }

  return (
    <FormSection
      title="Cancellation rule"
      description={`One rule for the business. Every ${label("group")} uses it.`}
    >
      <FormRow label="Free cancellation up to">
        <ChipRow
          label="Free cancellation up to"
          wrap
          options={[
            ...CUTOFF_HOURS.map((value) => ({ id: String(value), label: `${value} hours before` })),
            { id: "other", label: "Other" },
          ]}
          selectedId={showOther ? "other" : String(rules.freeCancelCutoffHours)}
          onSelect={pick}
        />
        {showOther ? (
          <div className="mt-3 max-w-[240px]">
            <Field
              label="Hours before the session"
              value={emptied ? "" : String(rules.freeCancelCutoffHours)}
              onChange={(event) => type(event.target.value)}
              onBlur={() => setEmptied(false)}
              inputMode="numeric"
              maxLength={3}
              placeholder={String(rules.freeCancelCutoffHours)}
              hint={
                emptied
                  ? `Type a number. Until you do, the rule stays at ${rules.freeCancelCutoffHours} hours.`
                  : `Any whole number from 0 to ${MAX_CUTOFF_HOURS}. 0 means up to the start time.`
              }
            />
          </div>
        ) : null}
      </FormRow>
      <FormRow label="After that">
        <ChipRow
          label="After that"
          wrap
          options={[
            { id: "session_lost", label: "The session is lost" },
            { id: "session_returned", label: "The session goes back" },
          ]}
          selectedId={rules.lateCancelRule}
          onSelect={(id) => onChange({ ...rules, lateCancelRule: id as BusinessRules["lateCancelRule"] })}
        />
        <p className="mt-2 text-[13px] text-ink-3">
          {rules.lateCancelRule === "session_lost"
            ? `A late cancellation counts the same as if the ${label("participant")} came.`
            : "A late cancellation costs nothing. You can still give each package a limit."}
        </p>
      </FormRow>
      <FormRow label={`What ${label("member", { plural: true })} read`}>
        <p
          aria-live="polite"
          data-testid="rule-preview"
          className="rounded-xl bg-accent-surface px-4 py-3 text-[15px] text-accent-strong"
        >
          {rulePreview(rules)}
        </p>
        <p className="mt-2 text-[13px] text-ink-3">
          When you cancel a session, every {label("participant")} gets it back. Always. That is not a
          setting.
        </p>
        <p className="mt-2 text-[13px] text-ink-3">
          To give {label("member", { plural: true })} a number of free cancellations, or free late
          cancellations, set it on each package.
        </p>
      </FormRow>
      {footer ? <div className="py-4">{footer}</div> : null}
    </FormSection>
  );
}
